import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useAnalytics } from '~/analytics';
import { useSidebarWidth } from '~/components/common/sidebarWidth';
import { useHeaderBlockHeight } from '~/components/global/appBarHeight';
import { ASSISTANT_NAME } from '~/components/otto/branding';
import { OttoAvatar } from '~/components/otto/OttoAvatar';
import { OttoComposer } from '~/components/otto/OttoComposer';
import { OttoConversation } from '~/components/otto/OttoConversation';
import { OttoHero } from '~/components/otto/OttoHero';
import { useOtto } from '~/components/otto/OttoProvider';
import { isSessionReady } from '~/components/otto/sessions';
import { SURFACE_RADIUS } from '~/theme';

const DOCK_WIDTH = 440;
const DOCK_HEIGHT = 800;
const DOCK_OFFSET = 24;
const HISTORY_PANEL_WIDTH = 280;

export function OttoPanel() {
  const theme = useTheme();
  const { state, close, dock, expand, toggleHistory, newChat } = useOtto();
  const { track } = useAnalytics();
  // Live width, not a constant: the sidebar collapses between an 81px rail and 248px
  // expanded, and pinned at 81 the expanded nav covered the panel's left 167px.
  const sidebarWidth = useSidebarWidth();
  const headerBlockHeight = useHeaderBlockHeight();
  const mode = state.mode;
  const isDock = mode === 'dock';

  const anyReady = useMemo(
    () =>
      state.sessionOrder.some((id) => {
        const s = state.sessions[id];
        return id !== state.activeSessionId && s && isSessionReady(s);
      }),
    [state.sessionOrder, state.sessions, state.activeSessionId],
  );

  // Open-time stamp for duration_ms on chat_panel_closed. Set the first
  // time mode becomes non-closed during this mount; cleared when the
  // close handler fires. The dock↔full transition doesn't reset it —
  // duration is measured from the original open, regardless of mode
  // changes in between.
  const panelOpenedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (mode !== 'closed' && panelOpenedAtRef.current == null) {
      panelOpenedAtRef.current = performance.now();
    }
  }, [mode]);

  const closeWithTracking = useCallback(() => {
    const openedAt = panelOpenedAtRef.current;
    track('chat_panel_closed', {
      mode: mode === 'dock' ? 'dock' : 'full',
      duration_ms: openedAt != null ? Math.round(performance.now() - openedAt) : 0,
      session_id: state.activeSessionId,
    });
    panelOpenedAtRef.current = null;
    close();
  }, [mode, state.activeSessionId, close, track]);

  const expandWithTracking = useCallback(() => {
    track('chat_panel_expanded', { session_id: state.activeSessionId });
    expand();
  }, [state.activeSessionId, expand, track]);

  useEffect(() => {
    if (mode === 'closed') return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // ESC in full mode downgrades to dock (not tracked — no spec'd
      // event for the downgrade transition). ESC in dock mode closes.
      if (mode === 'full') dock();
      else closeWithTracking();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mode, closeWithTracking, dock]);

  const panelRef = useRef<HTMLElement | null>(null);
  // Pull focus to the panel when it opens so that keyboard users land on
  // the modal surface — the wrapper has tabIndex=-1 specifically so this
  // is programmatic-only and doesn't add a keyboard tab stop.
  useEffect(() => {
    if (mode === 'closed') return;
    const el = panelRef.current;
    if (!el) return;
    if (el.contains(document.activeElement)) return;
    el.focus({ preventScroll: true });
  }, [mode]);

  if (mode === 'closed') return null;
  const isFull = mode === 'full';
  const session = state.activeSessionId ? state.sessions[state.activeSessionId] : null;
  const hasMessages = (session?.messages.length ?? 0) > 0;
  const historyOpen = state.historyOpen;

  return (
    <Box
      ref={panelRef}
      component={isFull ? 'div' : 'aside'}
      role={isFull ? 'dialog' : 'complementary'}
      aria-modal={isFull ? true : undefined}
      aria-label={`${ASSISTANT_NAME} assistant`}
      tabIndex={-1}
      sx={{
        ...(isDock
          ? {
              position: 'fixed',
              right: DOCK_OFFSET,
              bottom: DOCK_OFFSET,
              width: DOCK_WIDTH,
              height: DOCK_HEIGHT,
              maxHeight: `calc(100vh - ${DOCK_OFFSET * 2}px)`,
              maxWidth: `calc(100vw - ${DOCK_OFFSET * 2}px)`,
              borderRadius: `${SURFACE_RADIUS}px`,
              border: `1px solid ${alpha('#000', 0.08)}`,
              boxShadow: `0 20px 48px ${alpha('#000', 0.22)}`,
              zIndex: theme.zIndex.modal,
            }
          : {
              position: 'fixed',
              // The header grows past 150px once a filter row and its chips are showing —
              // pinned at 96/123 the panel covered the bottom of that row.
              top: `${headerBlockHeight}px`,
              bottom: 0,
              // Below `sm` the sidebar is a temporary overlay, so there is
              // nothing to sit beside.
              left: { xs: 0, sm: `${sidebarWidth}px` },
              right: historyOpen ? `${HISTORY_PANEL_WIDTH}px` : 0,
              // Matches the drawer's own collapse animation so the two edges move together.
              transition: theme.transitions.create('left', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              borderLeft: `1px solid ${alpha('#000', 0.08)}`,
              zIndex: theme.zIndex.drawer,
            }),
        background: hasMessages ? '#FAFBFC' : 'linear-gradient(180deg, #F0F6FF 0%, #F9FAFB 40%)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          background: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <OttoAvatar size={32} withStatusDot />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {ASSISTANT_NAME}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              Your portfolio assistant · online
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={() => newChat()} aria-label="New chat">
            <AddRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => toggleHistory()}
            aria-label="Toggle history"
            sx={{ position: 'relative' }}
          >
            <HistoryRoundedIcon sx={{ fontSize: 20 }} />
            {anyReady ? (
              <Box
                sx={{
                  position: 'absolute',
                  right: 4,
                  top: 4,
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: theme.palette.error.main,
                }}
              />
            ) : null}
          </IconButton>
          {mode === 'dock' ? (
            <IconButton
              size="small"
              onClick={expandWithTracking}
              aria-label={`Expand ${ASSISTANT_NAME}`}
            >
              <OpenInFullRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              onClick={() => dock()}
              aria-label={`Dock ${ASSISTANT_NAME} to side`}
            >
              <CloseFullscreenRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={closeWithTracking}
            aria-label={`Close ${ASSISTANT_NAME}`}
          >
            <CloseRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          py: hasMessages ? 3 : 0,
        }}
      >
        {state.activeSessionId && hasMessages ? (
          <OttoConversation key={state.activeSessionId} sessionId={state.activeSessionId} />
        ) : (
          <OttoHero />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexShrink: 0,
          px: 3,
          pt: 1,
          pb: 2.5,
          background: hasMessages
            ? `linear-gradient(0deg, #FAFBFC 60%, transparent 100%)`
            : 'transparent',
        }}
      >
        <OttoComposer />
      </Box>
    </Box>
  );
}
